import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { getLanguage, type TextDirection } from '@/i18n/languageRegistry';
import { changeAppLanguage } from '@/i18n/i18n';

export type OiLocale = 'en' | 'ar';

interface OwnerIntelligenceLocaleContextValue {
  locale: OiLocale;
  dir: TextDirection;
  setLocale: (locale: OiLocale) => void;
  toggleLocale: () => void;
}

const OwnerIntelligenceLocaleContext = createContext<OwnerIntelligenceLocaleContextValue | null>(null);

/**
 * Thin alias over platform ui_language — no parallel locale store.
 * Prefer LanguageSwitcher / UserPreferences for app-wide changes.
 */
export function OwnerIntelligenceLocaleProvider({ children }: { children: React.ReactNode }) {
  const { preferences, updatePreference } = useUserPreferences();
  const lang = getLanguage(preferences.ui_language || preferences.language);
  const locale: OiLocale = lang.code === 'ar' ? 'ar' : 'en';
  const dir = lang.dir;

  const setLocale = useCallback(
    (next: OiLocale) => {
      void updatePreference('ui_language', next);
      void changeAppLanguage(next);
    },
    [updatePreference]
  );

  const toggleLocale = useCallback(() => {
    setLocale(locale === 'en' ? 'ar' : 'en');
  }, [locale, setLocale]);

  const value = useMemo(
    () => ({ locale, dir, setLocale, toggleLocale }),
    [locale, dir, setLocale, toggleLocale]
  );

  return (
    <OwnerIntelligenceLocaleContext.Provider value={value}>
      {children}
    </OwnerIntelligenceLocaleContext.Provider>
  );
}

export function useOwnerIntelligenceLocale(): OwnerIntelligenceLocaleContextValue {
  const ctx = useContext(OwnerIntelligenceLocaleContext);
  if (!ctx) {
    return {
      locale: 'en',
      dir: 'ltr',
      setLocale: () => {},
      toggleLocale: () => {},
    };
  }
  return ctx;
}
